import { cn } from "@/lib/utils";
import { NoImage } from "@assets/constant/index.constant";
import { Switch } from "@components/ui/switch";
import { endpoint } from "@services/api.services";
import Tooltip from "@ui/external/components/tooltip";
import { ZoomImage } from "@ui/internal/components/formelement";
import { isNullCheck, showToast } from "@utils/helper/index.helper";
import { useHandleAction } from "@utils/hook/index.hook";
import { useSelectionStore } from "@utils/states/index.state";
import { formatDistanceToNow, isPast, parseISO } from "date-fns";
import { ClockIcon, CopyIcon } from "lucide-react";
import { useMemo } from "react";
interface CloumnListProps {
  refetch?: () => void;
  toggleActiveStatus: (id: string | number, state: boolean) => void;
}
const ColumnList = ({ refetch, toggleActiveStatus }: CloumnListProps) => {
  const CareerEndpoint = useMemo(() => endpoint["careers"], []);
  const { clearSelection } = useSelectionStore();
  const { handleAction } = useHandleAction({
    endpoint: CareerEndpoint,
    refetch,
  });
  const columns = useMemo(
    () => [
      {
        name: "ID",
        cell: (row: any) => (
          <span className="font-semibold text-gray-700">{row?.id}</span>
        ),
        sortable: true,
      },
      {
        name: <span className="flex items-center gap-2">Title</span>,
        cell: (row: any) => (
          <div className="flex items-center gap-3 w-full justify-between">
            <ZoomImage>
              <div className="border rounded-md hover:outline-[#1aa178]/50 hover:outline-2 overflow-hiddenbg-slate-100">
                <img
                  src={row?.image || NoImage}
                  alt="Cropped"
                  onError={(e: any) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = NoImage;
                  }}
                  className="object-cover size-12 border rounded-md"
                />
              </div>
            </ZoomImage>
            <Tooltip description={row?.title}>
              <div className="block w-fit max-w-[100px] truncate">
                {isNullCheck(row?.title) as string}
              </div>
            </Tooltip>
            <Tooltip description="Copy Application Link">
              <span className="p-2 rounded-full cursor-pointer group flex items-center hover:bg-[#1da077]">
                <CopyIcon
                  size={14}
                  className="group-hover:text-white"
                  onClick={() => {
                    window.navigator.clipboard.writeText(row?.apply_link);
                    showToast({
                      type: "success",
                      title: "Success",
                      description: "Copied to clipboard",
                    });
                  }}
                />
              </span>
            </Tooltip>
          </div>
        ),
        sortable: false,
      },
      {
        name: "Description",

        cell: (row: any) => (
          <>
            {row?.description ? (
              <div className="max-w-[200px] line-clamp-2 overflow-hidden">
                <p dangerouslySetInnerHTML={{ __html: row.description }}></p>
              </div>
            ) : (
              "N/A"
            )}
          </>
        ),
        sortable: true,
      },

      {
        name: "Actions",
        cell: (row: any) => {
          const futureDate =
            row?.expiration_date && parseISO(row?.expiration_date);

          const result = isPast(futureDate)
            ? "Expired"
            : formatDistanceToNow(futureDate, { addSuffix: true });
          return (
            <div className="flex w-full items-center -translate-x-14 justify-end gap-4 cursor-pointer font-[500] select-none">
              {row?.expiration_date && (
                <Tooltip description={result}>
                  <ClockIcon
                    size={14}
                    className={cn([
                      `cursor-pointer`,
                      result === "Expired" ? " text-[red]" : "",
                    ])}
                  />
                </Tooltip>
              )}

              <Tooltip description={"Toggle Visibility Status"}>
                <div>
                  {" "}
                  <Switch
                    checked={row?.is_show}
                    onCheckedChange={(value) =>
                      toggleActiveStatus(row?.slug, value)
                    }
                    className="cursor-pointer"
                  />
                </div>
              </Tooltip>

              <button
                className="text-[#1DA077] cursor-pointer"
                onClick={() =>
                  handleAction({ type: "page-edit", data: row.slug })
                }
                title="Edit"
              >
                Edit
              </button>
              <button
                className="text-[#C33241] cursor-pointer"
                onClick={() => {
                  clearSelection();
                  handleAction({
                    type: "confirm",
                    data: { type: "delete", id: row.slug },
                  });
                }}
                title="Delete"
              >
                Delete
              </button>
            </div>
          );
        },
        sortable: false,
      },
    ],
    [handleAction, clearSelection, toggleActiveStatus]
  );

  return columns;
};

export { ColumnList };
